import { Container, Texture, Sprite, Graphics } from "pixi.js"
import {
  TERRAIN_TILE_COUNT,
  TERRAIN_TILE_HEIGHT,
  TERRAIN_TILE_WIDTH,
} from "../../constants"
import { generateTerrain } from "./generate-terrain"
import { generateGroundObjects } from "./generate-ground-objects"
import { Foliage } from "./foliage"
const terrainWorker = new Worker(new URL("terrain-worker.js", import.meta.url))

async function Tile({ i }) {
  const tile = new Container()

  const pixels = generateTerrain({ offsetY: i * TERRAIN_TILE_HEIGHT })

  const sprite = new Sprite(await generateTexture(pixels))
  sprite.x = -TERRAIN_TILE_WIDTH / 2
  sprite.y = i * TERRAIN_TILE_HEIGHT
  sprite.width = TERRAIN_TILE_WIDTH
  sprite.height = TERRAIN_TILE_HEIGHT
  sprite.pixels = pixels
  tile.addChild(sprite)

  const groundObjects = await generateGroundObjects({
    offsetY: sprite.y,
    pixels,
  })
  // TODO: Once groundObjects can store multiple types, use .filter on them when
  //     | sending them in to Foliage.
  const foliage = await Foliage({ foliageObjects: groundObjects })
  tile.addChild(foliage.foliage)

  return { tile, sprite, foliage }
}

export async function Terrain({ player, onTileGenerated, onTileRemoved }) {
  const terrain = new Container()

  terrain.width = TERRAIN_TILE_WIDTH
  terrain.height = TERRAIN_TILE_HEIGHT

  let tiles = []

  const init = async () => {
    tiles = await Promise.all(
      [...Array(TERRAIN_TILE_COUNT)].map(async (_, i) => Tile({ i })),
    )
    terrain.addChild(...tiles.map(({ tile }) => tile))

    tiles.forEach(({ sprite }) => {
      onTileGenerated?.({
        x: sprite.x,
        y: sprite.y,
        width: TERRAIN_TILE_WIDTH,
        height: TERRAIN_TILE_HEIGHT,
      })
    })
  }

  const bounds = new Graphics()
    .rect(
      -TERRAIN_TILE_WIDTH,
      player.y - TERRAIN_TILE_HEIGHT,
      TERRAIN_TILE_WIDTH / 2,
      TERRAIN_TILE_HEIGHT * TERRAIN_TILE_COUNT,
    )
    .rect(
      TERRAIN_TILE_WIDTH / 2,
      player.y - TERRAIN_TILE_HEIGHT,
      TERRAIN_TILE_WIDTH + TERRAIN_TILE_WIDTH / 2,
      TERRAIN_TILE_HEIGHT * TERRAIN_TILE_COUNT,
    )
    .fill("#000")
  terrain.addChild(bounds)

  const tick = () => {
    if (!tiles.length) return

    const [topTile, bottomTile] = tiles.reduce(
      ([top, bottom], tile) => [
        tile.sprite.y < top.sprite.y ? tile : top,
        tile.sprite.y > bottom.sprite.y ? tile : bottom,
      ],
      [tiles[0], tiles[tiles.length - 1]],
    )

    if (player.y > topTile.sprite.y + TERRAIN_TILE_HEIGHT * 2) {
      const newY = topTile.sprite.y + TERRAIN_TILE_HEIGHT * TERRAIN_TILE_COUNT

      onTileRemoved?.({
        x: topTile.sprite.x,
        y: topTile.sprite.y,
        width: TERRAIN_TILE_WIDTH,
        height: TERRAIN_TILE_HEIGHT,
      })

      terrainWorker.postMessage({
        y: newY,
      })
      topTile.sprite.y = newY
    } else if (
      bottomTile.sprite.y > TERRAIN_TILE_HEIGHT * 2 &&
      player.y < bottomTile.sprite.y - TERRAIN_TILE_HEIGHT * 1
    ) {
      const newY =
        bottomTile.sprite.y - TERRAIN_TILE_HEIGHT * TERRAIN_TILE_COUNT

      onTileRemoved?.({
        x: bottomTile.sprite.x,
        y: bottomTile.sprite.y,
        width: TERRAIN_TILE_WIDTH,
        height: TERRAIN_TILE_HEIGHT,
      })

      terrainWorker.postMessage({
        y: newY,
      })
      bottomTile.sprite.y = newY
    }

    bounds.y = player.y

    // Hide tiles that are off-screen
    tiles.forEach(({ sprite, tile }) => {
      const screenY = sprite.y - player.y + window.Game.app.screen.height / 2
      tile.renderable =
        screenY <= window.Game.app.screen.height + 120 &&
        screenY + TERRAIN_TILE_HEIGHT >= 0
    })
  }

  terrainWorker.onmessage = async ({ data }) => {
    const { y, pixels, groundObjects } = data
    const tile = tiles.find(({ sprite }) => sprite.y === y)
    if (!tile) return

    tile.sprite.texture = await generateTexture(pixels)
    tile.sprite.pixels = pixels
    tile.tile.removeChild(tile.foliage.foliage)
    // TODO: Once groundObjects can store multiple types, use .filter on them
    //     | when sending them in to Foliage.
    let newFoliage = await Foliage({ foliageObjects: groundObjects })
    tile.foliage = newFoliage
    tile.tile.addChild(newFoliage.foliage)

    onTileGenerated?.({
      x: tile.sprite.x,
      y: tile.sprite.y,
      width: TERRAIN_TILE_WIDTH,
      height: TERRAIN_TILE_HEIGHT,
    })
  }

  const isSolid = ({ x: worldX, y: worldY }) => {
    worldX = Math.round(worldX)
    worldY = Math.round(worldY)

    const tile = tiles.find(
      ({ sprite }) =>
        sprite.y <= worldY && sprite.y + TERRAIN_TILE_HEIGHT >= worldY,
    )

    if (!tile) return false
    const { sprite } = tile

    const alphaPixelIndex =
      ((worldY - sprite.y) * TERRAIN_TILE_WIDTH + (worldX - sprite.x)) * 4 + 3

    return sprite.pixels[alphaPixelIndex] === 255
  }

  return { terrain, init, tick, isSolid }
}

export async function generateTexture(pixels) {
  const imageData = new ImageData(
    pixels,
    TERRAIN_TILE_WIDTH,
    TERRAIN_TILE_HEIGHT,
  )

  const bitmap = await createImageBitmap(imageData)

  return Texture.from(bitmap)
}
